/* FE report download (no backend export endpoint) — shared by the audit report
   page and the reports list. loadReportForDownload() streams + maps a payload
   for callers with no report loaded; downloadReport() builds the HTML and
   saves it, bundling evidence files + OPA json into a .zip when requested. */
import { streamReport, fetchReportExecutiveSummary, fetchEvidenceBase64 } from "./reportApi";
import { mapReportData } from "../utils/mapReportData";
import type { ReportPayload } from "../types/reportTypes";
import {
  buildAuditReportHtml,
  collectEvidenceResourceIds,
  type EmbeddedEvidence,
  type ExecSummaryState,
  type ReportAudience,
} from "../utils/buildAuditReportHtml";
import { createZip, base64ToBytes, type ZipEntry } from "../../../../utils/zip";

const slug = (name: string) => name.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "") || "audit_report";

/** "YYYY-MM-DD_HH-MM-SS" at call time — makes every download's filename unique so
    filename-based attachment caching (email/Teams/Slack/shared drives) can't serve
    a stale report. */
const timestamp = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}-${p(d.getMinutes())}-${p(d.getSeconds())}`;
};

const EXT_BY_TYPE: Record<string, string> = {
  "application/pdf": ".pdf",
  "image/png": ".png",
  "image/jpeg": ".jpg",
  "image/gif": ".gif",
  "text/plain": ".txt",
  "text/csv": ".csv",
};
const extFor = (ct?: string) => EXT_BY_TYPE[(ct || "").split(";")[0].trim().toLowerCase()] || "";

export interface LoadedReport {
  payload: ReportPayload;
  execState: ExecSummaryState;
  execText: string;
}

/** Streams the main payload + AI summary in parallel (used by the reports list). */
export async function loadReportForDownload(sspId: string): Promise<LoadedReport> {
  const [raw, summary] = await Promise.all([
    streamReport(sspId, () => {}),
    fetchReportExecutiveSummary(sspId).then((t) => ({ ok: true, t })).catch(() => ({ ok: false, t: "" })),
  ]);
  return {
    payload: mapReportData(raw, sspId),
    execState: summary.ok ? "ready" : "error",
    execText: summary.t,
  };
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 3000); // defer so large downloads aren't cut short
}

export interface DownloadResult {
  evidenceFetched: number;
  evidenceTotal: number;
}

/** Build + save the report from an already-loaded payload. */
export async function downloadReport(
  payload: ReportPayload,
  opts: {
    execState: ExecSummaryState;
    execText: string;
    withEvidence: boolean;
    audience?: ReportAudience;
  },
): Promise<DownloadResult> {
  const { execState, execText, withEvidence, audience = "system_owner" } = opts;
  const audienceSuffix = audience === "auditor" ? "_auditor" : "";
  const base = `${slug(payload.meta.sspName)}${audienceSuffix}_${timestamp()}`;
  const execSummary = { state: execState, text: execText };

  // Without evidence → just the self-contained HTML.
  if (!withEvidence) {
    const doc = buildAuditReportHtml(payload, { execSummary, audience });
    triggerDownload(new Blob([doc], { type: "text/html;charset=utf-8" }), `${base}.html`);
    return { evidenceFetched: 0, evidenceTotal: 0 };
  }

  // With evidence → zip: HTML (evidence embedded) + evidence/ files + OPA json.
  const ids = collectEvidenceResourceIds(payload);
  const fetched = await Promise.all(
    ids.map((id) => fetchEvidenceBase64(id).then((r) => ({ id, ...r })).catch(() => null)),
  );

  const embeddedEvidence: EmbeddedEvidence = {};
  const entries: ZipEntry[] = [];
  const used = new Set<string>();
  for (const r of fetched) {
    if (!r) continue;
    embeddedEvidence[r.id] = { fileName: r.fileName, contentType: r.contentType, base64: r.base64 };
    let name = (r.fileName || r.id).replace(/[^\w.\-]+/g, "_");
    if (!/\.[a-z0-9]+$/i.test(name)) name += extFor(r.contentType);
    let unique = name;
    for (let n = 1; used.has(unique.toLowerCase()); n++) {
      const dot = name.lastIndexOf(".");
      unique = dot > 0 ? `${name.slice(0, dot)}_${n}${name.slice(dot)}` : `${name}_${n}`;
    }
    used.add(unique.toLowerCase());
    entries.push({ name: `evidence/${unique}`, data: base64ToBytes(r.base64) });
  }

  // Automated (OPA) evidence — assets + rules already in the payload.
  const parseAssets = (s?: string) => { try { return s ? JSON.parse(s) : []; } catch { return []; } };
  for (const rr of payload.assessment.results) {
    const failedAssets = parseAssets(rr.failedAssetsJson);
    const passedAssets = parseAssets(rr.passedAssetsJson);
    if (!failedAssets.length && !passedAssets.length) continue;
    const obj = {
      controlId: rr.controlId, controlTitle: rr.controlTitle, statementId: rr.statementId,
      determination: rr.determination,
      failedAssetCount: rr.failedAssetCount ?? failedAssets.length,
      passedAssetCount: rr.passedAssetCount ?? passedAssets.length,
      failedAssets, passedAssets,
    };
    let opaName = `${(rr.controlId || "control").replace(/[^\w.\-]+/g, "_")}.json`;
    for (let n = 1; used.has(("opa/" + opaName).toLowerCase()); n++) {
      opaName = `${(rr.controlId || "control").replace(/[^\w.\-]+/g, "_")}_${n}.json`;
    }
    used.add(("opa/" + opaName).toLowerCase());
    entries.push({ name: `evidence/opa/${opaName}`, data: new TextEncoder().encode(JSON.stringify(obj, null, 2)) });
  }

  const doc = buildAuditReportHtml(payload, { execSummary, embeddedEvidence, audience });
  const zip = createZip([{ name: `${base}.html`, data: new TextEncoder().encode(doc) }, ...entries]);
  triggerDownload(zip, `${base}.zip`);
  return { evidenceFetched: fetched.filter(Boolean).length, evidenceTotal: ids.length };
}
